import React, { useState } from "react";
import { Platform, ScrollView, StyleSheet, TextInput, View } from "react-native";
import { MicrophoneIcon, SpeakerHighIcon, SpeakerSlashIcon, PlusIcon } from "@phosphor-icons/react";
import { Send } from "lucide-react";
import { AttachmentChip } from "../../chatNewInterface/interfaceComponents/attachmentChip/AttachmentChip";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { useAccessibilityContext } from "../../../contexts/AccessibilityContext";
import {
  colors,
  radii,
  shadowStyles,
  spacing,
  typographyScale,
  typographyStyles,
  webTransitions,
} from "../../../styles/design-system";

interface AITutorChatBarProps {
  value: string;
  isRecording: boolean;
  isAudioOn: boolean;
  placeholder: string;
  inputRef?: React.RefObject<TextInput>;
  onChangeText: (text: string) => void;
  onSubmitEditing: () => void;
  isDisabled: boolean;
  onRecordingStartPress: () => void;
  onRecordingStopPress: () => void;
  onAudioOnPress: () => void;
  onAudioOffPress: () => void;
  isAttachmentEnabled: boolean;
  attachment: { id: string; title: string } | null;
  isAttachmentLoading: boolean;
  onAttachmentPress: () => void;
  onDeleteAttachmentPress: () => void;
}

const INPUT_FIXED_HEIGHT = 55;
const INPUT_VERTICAL_PADDING = (INPUT_FIXED_HEIGHT - typographyScale.textSm.lineHeight) / 2;
const CHAT_BAR_HEIGHT = 88;
const ATTACHMENT_ROW_HEIGHT = 48;
const CHAT_BAR_MAX_HEIGHT_WITH_ATTACHMENT = CHAT_BAR_HEIGHT + ATTACHMENT_ROW_HEIGHT + spacing.sm;

const AITutorChatBar: React.FC<AITutorChatBarProps> = ({
  value,
  isRecording,
  isAudioOn,
  placeholder,
  inputRef,
  onChangeText,
  onSubmitEditing,
  isDisabled,
  onRecordingStartPress,
  onRecordingStopPress,
  onAudioOnPress,
  onAudioOffPress,
  isAttachmentEnabled,
  attachment,
  isAttachmentLoading,
  onAttachmentPress,
  onDeleteAttachmentPress,
}) => {
  const { isMedium } = useResponsiveScreen();
  const { screenReaderIsEnabled } = useAccessibilityContext();
  const [isFocused, setIsFocused] = useState(false);

  const handleChangeText = (text) => {
    onChangeText(text);
  };

  const handleKeyPress = (event) => {
    if (isDisabled) {
      return;
    }
    if (screenReaderIsEnabled) {
      return;
    }
    if (event.nativeEvent.key === "Enter" && !event.nativeEvent.shiftKey) {
      event.preventDefault();
      onSubmitEditing();
    }
  };

  const handleMicPress = () => {
    if (isRecording) {
      onRecordingStopPress();
      return;
    }
    onRecordingStartPress();
  };

  return (
    <View style={styles.wrapper}>
      <View style={[styles.surface, attachment ? styles.surfaceWithAttachment : styles.surfaceFixedHeight]}>
        <View style={styles.content}>
          <View style={[styles.inputShell, isFocused && styles.inputShellFocused]}>
            {attachment ? (
              <View style={styles.attachmentRow}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.attachmentScrollContent}
                  style={styles.attachmentScroll}>
                  <AttachmentChip
                    type="file"
                    name={attachment.title}
                    isLoading={isAttachmentLoading}
                    removeChip={onDeleteAttachmentPress}
                  />
                </ScrollView>
              </View>
            ) : null}
            <View style={[styles.inputRow, isMedium ? styles.inputRowDesktop : styles.inputRowMobile]}>
              {isAttachmentEnabled ? (
                <CustomButton
                  text=""
                  rightIcon={null}
                  textStyle={{}}
                  textProps={{}}
                  styleType="transparent"
                  leftIcon={<PlusIcon size={22} color={colors.grey[500]} weight="bold" />}
                  onPress={onAttachmentPress}
                  disabled={isAttachmentLoading || isDisabled}
                  style={[styles.iconButton, (isAttachmentLoading || isDisabled) && styles.iconButtonDisabled]}
                  accessibilityLabel="Attach file"
                />
              ) : null}
              <TextInput
                ref={inputRef}
                value={value}
                maxLength={7500}
                multiline
                numberOfLines={1}
                placeholder={placeholder}
                placeholderTextColor={colors.grey[400]}
                style={[styles.textInput, { height: INPUT_FIXED_HEIGHT }]}
                onChangeText={handleChangeText}
                onSubmitEditing={onSubmitEditing}
                onKeyPress={handleKeyPress}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                scrollEnabled
                editable={!isDisabled}
                aria-label="Chat text input"
                accessibilityLabel="Chat text input"
                accessibilityHint="Enter your message here"
              />
              {value.trim() ? (
                <CustomButton
                  text=""
                  rightIcon={null}
                  textStyle={{}}
                  textProps={{}}
                  styleType="transparent"
                  leftIcon={<Send size={20} color={colors.white} />}
                  onPress={onSubmitEditing}
                  disabled={isDisabled}
                  style={[styles.iconButton, styles.sendButton]}
                  accessibilityLabel="Send message"
                  accessibilityHint="Press to send your message"
                />
              ) : (
                <CustomButton
                  text=""
                  rightIcon={null}
                  textStyle={{}}
                  textProps={{}}
                  styleType="transparent"
                  leftIcon={
                    <MicrophoneIcon
                      size={20}
                      color={isRecording ? colors.brand[600] : colors.grey[500]}
                      weight={isRecording ? "fill" : "regular"}
                    />
                  }
                  onPress={handleMicPress}
                  disabled={isDisabled}
                  style={styles.iconButton}
                  accessibilityLabel={isRecording ? "Stop recording" : "Start recording"}
                />
              )}
              <CustomButton
                text=""
                rightIcon={null}
                textStyle={{}}
                textProps={{}}
                styleType="transparent"
                leftIcon={
                  isAudioOn ? (
                    <SpeakerHighIcon size={20} color={colors.grey[500]} weight="regular" />
                  ) : (
                    <SpeakerSlashIcon size={20} color={colors.grey[500]} weight="regular" />
                  )
                }
                onPress={isAudioOn ? onAudioOffPress : onAudioOnPress}
                disabled={isDisabled}
                style={styles.iconButton}
                accessibilityLabel={isAudioOn ? "Mute voice" : "Unmute voice"}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 30,
  },
  surface: {
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginTop: 0,
    ...shadowStyles.sm,
    // @ts-expect-error - web only
    backdropFilter: "blur(12px)",
  },
  surfaceFixedHeight: {
    height: CHAT_BAR_HEIGHT,
  },
  surfaceWithAttachment: {
    height: CHAT_BAR_MAX_HEIGHT_WITH_ATTACHMENT,
  },
  content: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
  },
  inputShell: {
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.full,
    ...webTransitions.fast,
  },
  inputShellFocused: {
    borderColor: colors.brand[600],
    boxShadow: "0 0 0 2px rgba(62, 104, 254, 0.4)",
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  inputRowDesktop: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 0,
  },
  inputRowMobile: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 0,
  },
  textInput: {
    flex: 1,
    minHeight: INPUT_FIXED_HEIGHT,
    maxHeight: INPUT_FIXED_HEIGHT,
    ...typographyStyles.textSm,
    color: colors.grey[700],
    paddingVertical: INPUT_VERTICAL_PADDING,
    paddingHorizontal: spacing.xs,
    textAlignVertical: "center",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    zIndex: 0,
    minWidth: 0,
  },
  iconButton: {
    padding: spacing.xs,
    borderRadius: radii.sm,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  sendButton: {
    backgroundColor: colors.brand[600],
    borderRadius: radii.xl,
  },
  iconButtonDisabled: {
    opacity: 0.5,
  },
  attachmentRow: {
    maxHeight: ATTACHMENT_ROW_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  attachmentScroll: {
    flexGrow: 0,
  },
  attachmentScrollContent: {
    flexDirection: "row",
    gap: spacing.xs,
    alignItems: "center",
  },
});

export default AITutorChatBar;
